/**
 * Layout maths of `NxTimeGrid`: where an entry sits in a day column, how
 * overlapping entries share the width, which entries go to the all-day lane.
 * Pure numbers, no DOM — the component turns them into percentages.
 *
 * Positions are WALL-CLOCK minutes (hours × 60 + minutes), not elapsed time:
 * on a daylight-saving day the column keeps its 24 labelled hours, and an
 * entry from 01:00 to 04:00 is drawn from the 01:00 line to the 04:00 line
 * although only two hours pass.
 */
import { addLocalDays, effectiveEndMs, fromIsoDate, hasValidStart } from './eventDays';
import type { NxCalendarEvent } from './types';

const MINUTES_PER_DAY = 24 * 60;

/**
 * The smallest duration an entry occupies when overlaps are worked out. A
 * point-in-time entry is drawn at a minimum height; if the overlap maths
 * ignored that height, the next entry would be placed on top of it. This is
 * the default; a view that knows its hour height passes the real value to
 * `layoutDayColumn` (`minMinutes`).
 */
export const MIN_EVENT_MINUTES = 15;

/** Wall-clock minutes since local midnight. */
export function wallMinutes(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

/**
 * An entry belongs in the all-day lane rather than a column: all-day entries,
 * and timed ones lasting a whole day or more, which would fill every column.
 */
export function belongsInLane(event: NxCalendarEvent): boolean {
  if (event.allDay) return true;
  return effectiveEndMs(event) - event.start.getTime() >= MINUTES_PER_DAY * 60_000;
}

/** One entry's piece within a day column, in minutes and overlap tracks. */
export interface NxTimeGridSegment {
  event: NxCalendarEvent;
  /** Wall-clock minutes of the drawn piece, clipped to the visible hours. */
  startMinute: number;
  endMinute: number;
  /** The entry goes on before this piece (previous day or above the range). */
  continuesBefore: boolean;
  /** …and after it. */
  continuesAfter: boolean;
  /** Overlap track, 0-based, and how many tracks its cluster has. */
  track: number;
  tracks: number;
  /** Tracks to the right it may widen into because they are free alongside it. */
  span: number;
}

export interface NxTimeGridColumnLayout {
  segments: NxTimeGridSegment[];
  /** Timed entries of this day that lie completely outside the visible hours. */
  outside: NxCalendarEvent[];
}

/**
 * Lays out one day column: every timed entry touching the day, clipped to the
 * day and to `[startHour, endHour)`, arranged side by side where they overlap.
 */
export function layoutDayColumn(
  events: readonly NxCalendarEvent[],
  dateIso: string,
  startHour: number,
  endHour: number,
  minMinutes: number = MIN_EVENT_MINUTES
): NxTimeGridColumnLayout {
  const day = fromIsoDate(dateIso);
  if (!day) return { segments: [], outside: [] };
  const dayStart = day.getTime();
  const dayEnd = addLocalDays(day, 1).getTime();
  const rangeStart = startHour * 60;
  const rangeEnd = endHour * 60;

  const segments: NxTimeGridSegment[] = [];
  const outside: NxCalendarEvent[] = [];
  for (const event of events) {
    if (!hasValidStart(event) || belongsInLane(event)) continue;
    const start = event.start.getTime();
    const end = effectiveEndMs(event);
    if (start >= dayEnd || end <= dayStart) continue;

    const rawStart = start <= dayStart ? 0 : wallMinutes(event.start);
    // A point in time keeps its own minute; a piece running into the next day
    // ends at the column's foot.
    const rawEnd =
      end >= dayEnd ? MINUTES_PER_DAY : Math.max(rawStart, wallMinutes(new Date(end - 1)) + 1);
    const pointInTime = !event.end || event.end.getTime() <= start;
    const visibleEnd = pointInTime ? rawStart + 1 : rawEnd;

    if (visibleEnd <= rangeStart || rawStart >= rangeEnd) {
      outside.push(event);
      continue;
    }
    const startMinute = Math.max(rawStart, rangeStart);
    const endMinute = pointInTime ? startMinute : Math.min(rawEnd, rangeEnd);
    segments.push({
      event,
      startMinute,
      endMinute,
      continuesBefore: start < dayStart || rawStart < rangeStart,
      continuesAfter: end > dayEnd || rawEnd > rangeEnd,
      track: 0,
      tracks: 1,
      span: 1,
    });
  }

  arrangeTracks(segments, Math.max(1, minMinutes));
  return { segments, outside };
}

function occupiedEnd(segment: NxTimeGridSegment, minMinutes: number): number {
  return Math.max(segment.endMinute, segment.startMinute + minMinutes);
}

function overlaps(a: NxTimeGridSegment, b: NxTimeGridSegment, minMinutes: number): boolean {
  return a.startMinute < occupiedEnd(b, minMinutes) && b.startMinute < occupiedEnd(a, minMinutes);
}

/**
 * Greedy track assignment per cluster of transitively overlapping entries —
 * the layout every calendar reader already knows. Each entry then widens to
 * the right over tracks nobody in its cluster uses alongside it.
 */
function arrangeTracks(segments: NxTimeGridSegment[], minMinutes: number): void {
  segments.sort(
    (a, b) =>
      a.startMinute - b.startMinute ||
      occupiedEnd(b, minMinutes) - occupiedEnd(a, minMinutes) ||
      String(a.event.id).localeCompare(String(b.event.id))
  );

  let cluster: NxTimeGridSegment[] = [];
  let clusterEnd = -1;
  let trackEnds: number[] = [];

  const closeCluster = () => {
    const tracks = trackEnds.length;
    for (const segment of cluster) {
      segment.tracks = tracks;
      let span = 1;
      for (let track = segment.track + 1; track < tracks; track += 1) {
        const blocked = cluster.some(
          (other) =>
            other !== segment && other.track === track && overlaps(other, segment, minMinutes)
        );
        if (blocked) break;
        span += 1;
      }
      segment.span = span;
    }
    cluster = [];
    trackEnds = [];
  };

  for (const segment of segments) {
    if (cluster.length && segment.startMinute >= clusterEnd) closeCluster();
    let track = trackEnds.findIndex((end) => end <= segment.startMinute);
    if (track === -1) {
      track = trackEnds.length;
      trackEnds.push(0);
    }
    trackEnds[track] = occupiedEnd(segment, minMinutes);
    segment.track = track;
    cluster.push(segment);
    clusterEnd = Math.max(cluster.length === 1 ? 0 : clusterEnd, occupiedEnd(segment, minMinutes));
  }
  if (cluster.length) closeCluster();
}

/** One bar in the all-day lane. */
export interface NxTimeGridLaneItem {
  event: NxCalendarEvent;
  /** First column (index into `dates`) and how many columns it covers. */
  column: number;
  span: number;
  /** Stacking row, 0-based. */
  row: number;
  /** Shown in the lane only because it lies outside the visible hours. */
  outsideHours: boolean;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

/**
 * The all-day lane: all-day and day-long entries as bars across the columns
 * they cover, plus the timed entries a column could not show, each stacked
 * into the first row that is free over its whole width.
 */
export function layoutAllDayLane(
  events: readonly NxCalendarEvent[],
  dates: readonly string[],
  outsideByColumn: ReadonlyArray<readonly NxCalendarEvent[]> = []
): { items: NxTimeGridLaneItem[]; rows: number } {
  const days = dates.map((iso) => fromIsoDate(iso));
  const pending: Omit<NxTimeGridLaneItem, 'row'>[] = [];

  for (const event of events) {
    if (!hasValidStart(event) || !belongsInLane(event)) continue;
    const start = event.start.getTime();
    const end = effectiveEndMs(event);
    let first = -1;
    let last = -1;
    days.forEach((day, index) => {
      if (!day) return;
      const from = day.getTime();
      const to = addLocalDays(day, 1).getTime();
      if (start < to && end > from) {
        if (first === -1) first = index;
        last = index;
      }
    });
    if (first === -1) continue;
    const firstDay = days[first]!;
    const lastDay = days[last]!;
    pending.push({
      event,
      column: first,
      span: last - first + 1,
      outsideHours: false,
      continuesBefore: start < firstDay.getTime(),
      continuesAfter: end > addLocalDays(lastDay, 1).getTime(),
    });
  }

  outsideByColumn.forEach((list, column) => {
    for (const event of list) {
      pending.push({
        event,
        column,
        span: 1,
        outsideHours: true,
        continuesBefore: false,
        continuesAfter: false,
      });
    }
  });

  pending.sort(
    (a, b) =>
      a.column - b.column ||
      Number(a.outsideHours) - Number(b.outsideHours) ||
      b.span - a.span ||
      a.event.start.getTime() - b.event.start.getTime()
  );

  const rowEnds: number[] = [];
  const items = pending.map((item) => {
    let row = rowEnds.findIndex((end) => end <= item.column);
    if (row === -1) {
      row = rowEnds.length;
      rowEnds.push(0);
    }
    rowEnds[row] = item.column + item.span;
    return { ...item, row };
  });
  return { items, rows: rowEnds.length };
}

/** The slot a minute of a day falls into, as start/end `Date`s. */
export function slotBounds(
  dateIso: string,
  minute: number,
  slotMinutes: number
): { start: Date; end: Date } | null {
  const day = fromIsoDate(dateIso);
  if (!day) return null;
  const startMinute = Math.floor(minute / slotMinutes) * slotMinutes;
  const start = new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    Math.floor(startMinute / 60),
    startMinute % 60
  );
  const endMinute = startMinute + slotMinutes;
  const end = new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    Math.floor(endMinute / 60),
    endMinute % 60
  );
  return { start, end };
}
